import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../../contexts/AuthContext';
import { useSupplierCrmOrgStore } from '../../../store/supplierCrmOrg.store';
import { findSuppliers, Supplier as DiscoveredSupplier } from '../../../services/supplierFinderService';
import suppliersService, { SupplierInput } from '../../../services/supplierCrm/suppliersService';

const DiscoverTab: React.FC = () => {
  const { user } = useAuth();
  const { activeOrgId } = useSupplierCrmOrgStore();
  const [product, setProduct] = useState('');
  const [location, setLocation] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<DiscoveredSupplier[]>([]);
  const [saving, setSaving] = useState<Set<number>>(new Set());

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!product.trim() || !location.trim()) {
      toast.error('Please enter both product and location');
      return;
    }

    if (!user?.id) {
      toast.error('User not authenticated');
      return;
    }

    setLoading(true);
    try {
      const response = await findSuppliers({
        product: product.trim(),
        location: location.trim(),
        userId: user.id
      });
      setResults(response.suppliers || []);
      toast.success(`Found ${response.suppliers.length} suppliers`);
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Failed to find suppliers');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveToCRM = async (supplier: DiscoveredSupplier, index: number) => {
    if (!activeOrgId) {
      toast.error('Please select an organization first');
      return;
    }

    setSaving(prev => new Set(prev).add(index));
    
    try {
      const payload: SupplierInput = {
        organization_id: activeOrgId,
        name: supplier.name,
        email: supplier.contactInfo,
        website: supplier.website,
        notes: supplier.description,
        category: undefined,
        status: 'active'
      };

      await suppliersService.create(payload);
      toast.success(`${supplier.name} saved to CRM`);
      
      // Remove from results after saving
      setResults(prev => prev.filter((_, i) => i !== index));
    } catch (error: any) {
      console.error('Save error:', error);
      toast.error(error?.message || 'Failed to save supplier');
    } finally {
      setSaving(prev => {
        const newSet = new Set(prev);
        newSet.delete(index);
        return newSet;
      });
    }
  };

  const handleSaveAll = async () => {
    if (!activeOrgId) {
      toast.error('Please select an organization first');
      return;
    }

    if (results.length === 0) {
      toast.error('No suppliers to save');
      return;
    }

    let saved = 0;
    let failed = 0;

    for (const supplier of results) {
      try {
        const payload: SupplierInput = {
          organization_id: activeOrgId,
          name: supplier.name,
          email: supplier.contactInfo,
          website: supplier.website,
          notes: supplier.description,
          status: 'active'
        };
        await suppliersService.create(payload);
        saved++;
      } catch (error) {
        failed++;
        console.error(`Failed to save ${supplier.name}:`, error);
      }
    }

    if (saved > 0) {
      toast.success(`Saved ${saved} supplier${saved > 1 ? 's' : ''} to CRM`);
      setResults([]);
    }
    
    if (failed > 0) {
      toast.error(`Failed to save ${failed} supplier${failed > 1 ? 's' : ''}`);
    }
  };

  return (
    <div className="space-y-6">
      {/* Search Form */}
      <div className="rounded-xl bg-gradient-to-r from-primary/5 to-secondary/5 p-6 ring-1 ring-primary/10">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-900">🤖 AI-Powered Supplier Discovery</h2>
          <p className="mt-1 text-sm text-gray-600">
            Find vetted suppliers worldwide using AI. Save them directly to your CRM with one click.
          </p>
        </div>

        <form onSubmit={handleSearch} className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                What are you looking for?
              </label>
              <input
                type="text"
                className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                placeholder="e.g., Licensed Disney merchandise wholesale"
                value={product}
                onChange={(e) => setProduct(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Location
              </label>
              <input
                type="text"
                className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                placeholder="e.g., United Kingdom or Global"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 font-semibold text-white shadow-sm transition hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Searching...</span>
                </>
              ) : (
                <>
                  <span>🔎</span>
                  <span>Discover Suppliers</span>
                </>
              )}
            </button>

            {results.length > 0 && (
              <button
                type="button"
                onClick={handleSaveAll}
                className="inline-flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 px-6 py-3 font-semibold text-green-700 transition hover:bg-green-100"
              >
                <span>💾</span>
                <span>Save All {results.length} to CRM</span>
              </button>
            )}
          </div>
        </form>

        {/* Search Tips */}
        <div className="mt-4 rounded-lg bg-blue-50 p-4 text-sm text-blue-700">
          <strong>💡 Tips:</strong>
          <ul className="mt-1 ml-4 list-disc space-y-1">
            <li>Be specific about products or services</li>
            <li>Include keywords like "wholesale", "manufacturer", or "distributor"</li>
            <li>For licensed products, include brand names</li>
          </ul>
        </div>
      </div>

      {/* Results */}
      {results.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              ✨ AI Discovery Results ({results.length})
            </h3>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {results.map((supplier, index) => (
              <div
                key={index}
                className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition hover:shadow-md"
              >
                <div className="mb-3">
                  <h4 className="text-lg font-semibold text-gray-900">{supplier.name}</h4>
                  {supplier.location && (
                    <p className="mt-1 text-sm text-gray-500">📍 {supplier.location}</p>
                  )}
                </div>

                {supplier.description && (
                  <p className="mb-3 text-sm text-gray-600 line-clamp-3">{supplier.description}</p>
                )}

                <div className="mb-4 flex flex-wrap gap-2 text-xs">
                  {supplier.contactInfo && (
                    <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 font-medium text-gray-700">
                      📧 {supplier.contactInfo}
                    </span>
                  )}
                  {supplier.website && (
                    <a
                      href={supplier.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 font-medium text-blue-700 hover:bg-blue-200"
                    >
                      🌐 Visit Website
                    </a>
                  )}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleSaveToCRM(supplier, index)}
                    disabled={saving.has(index)}
                    className="flex-1 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving.has(index) ? 'Saving...' : '💾 Save to CRM'}
                  </button>
                  {supplier.website && (
                    <a
                      href={supplier.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
                    >
                      🔗
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && results.length === 0 && (
        <div className="rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-12 text-center">
          <div className="text-4xl mb-4">🔎</div>
          <h3 className="text-lg font-semibold text-gray-900">Ready to Discover Suppliers</h3>
          <p className="mt-2 text-sm text-gray-600">
            Enter your search criteria above to find suppliers using AI.
          </p>
        </div>
      )}
    </div>
  );
};

export default DiscoverTab;
